// src/lib/micGuidance.js
// STATUS: active | UPDATED: 2026-09-03
// LOG:
//   2026-09-03  New. Tell somebody where the setting actually is.
//
// "Microphone blocked. Allow it in your browser settings." is true and useless.
// Tyler hit it on the first real attempt and could not get past it, and he
// built the thing. A salesperson between customers gets one shot at this, and
// if the first tap ends in a dead end he does not come back to it.
//
// WHY THE BROWSER MATTERS SO MUCH HERE
//
// Every browser hides this setting somewhere different, and once a person has
// denied it the browser will never prompt again. There is no way to ask a
// second time from JavaScript. So the only way out is a settings page the
// person has to find by hand, which means naming the exact taps.
//
// The macOS trap is worth its own line. Somebody can allow the microphone for
// the site and still be blocked, because the browser itself has not been
// allowed by the operating system. That is two separate switches with the same
// symptom, and nobody guesses the second one.

const IOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
const ANDROID = /Android/.test(navigator.userAgent)
const MAC = navigator.platform?.toUpperCase().includes('MAC') && !IOS

// Order matters. Chrome on iOS says CriOS, Edge says Edg, and both also say
// Safari or Chrome in the same string, so the specific tests come first.
function browser() {
  const ua = navigator.userAgent
  if (/CriOS/.test(ua)) return 'chrome-ios'
  if (/FxiOS/.test(ua)) return 'firefox-ios'
  if (/Edg\//.test(ua)) return 'edge'
  if (/Firefox\//.test(ua)) return 'firefox'
  if (/Chrome\//.test(ua)) return ANDROID ? 'chrome-android' : 'chrome'
  if (/Safari\//.test(ua)) return IOS ? 'safari-ios' : 'safari'
  return 'other'
}

// The OS half of the problem, on the two platforms that have one.
const macSystemStep = 'If it is still blocked, open System Settings, then Privacy and Security, then Microphone, and switch this browser on there too.'

// A page can be forbidden the microphone by its own Permissions-Policy header,
// and when it is, nothing the person does in browser or system settings has any
// effect. The Permissions API reports plain "denied" either way, so without this
// check the product confidently sends somebody to a settings page that cannot
// help them. It did exactly that to Tyler, three times, on 3 September.
//
// Not defensive programming for its own sake: this is the one microphone
// failure that is our fault rather than theirs, and it should read that way.
export function micBlockedByPolicy() {
  try {
    const policy = document.permissionsPolicy || document.featurePolicy
    if (!policy?.allowsFeature) return false
    return !policy.allowsFeature('microphone')
  } catch {
    return false
  }
}

export function micGuidance() {
  if (micBlockedByPolicy()) {
    return {
      title: 'The microphone is switched off on our side',
      steps: [
        'This is not your browser and not your settings.',
        'The site is sending a header that forbids the microphone, so nothing you change will help.',
        'Tell Tyler and it is a one line fix.',
      ],
      ours: true,
    }
  }

  switch (browser()) {
    case 'safari-ios':
      return {
        title: 'Safari is blocking the microphone',
        steps: [
          'Tap the aA button at the left of the address bar.',
          'Tap Website Settings.',
          'Set Microphone to Allow.',
          'Come back here and hold the mic again.',
        ],
        note: 'If Microphone is not listed, open the Settings app, then Safari, then Camera and Microphone, and allow it there.',
      }
    case 'chrome-ios':
      return {
        title: 'Chrome is blocking the microphone',
        steps: [
          'Open the Settings app on your phone.',
          'Scroll down to Chrome and tap it.',
          'Switch Microphone on.',
          'Come back here and hold the mic again.',
        ],
      }
    case 'chrome-android':
      return {
        title: 'Chrome is blocking the microphone',
        steps: [
          'Tap the icon just left of the web address at the top.',
          'Tap Permissions, then Microphone.',
          'Choose Allow.',
          'Reload the page and hold the mic again.',
        ],
      }
    case 'chrome':
    case 'edge': {
      // The obvious route is a dead end and Tyler proved it. Chrome only lists
      // Microphone in that popup once the site has actually asked, and a site
      // whose permission is already denied never asks, so the entry he was told
      // to look for does not exist. Site settings always has it.
      const scheme = browser() === 'edge' ? 'edge' : 'chrome'
      return {
        title: 'Your browser is blocking the microphone',
        steps: [
          'Click the icon just left of the web address.',
          'Click Site settings. It opens in a new tab.',
          'Find Microphone in the list and change it to Allow.',
          'Come back here and reload the page.',
        ],
        paste: {
          label: 'Or paste this into the address bar to go straight there:',
          value: `${scheme}://settings/content/siteDetails?site=${encodeURIComponent(window.location.origin)}`,
        },
        note: MAC ? macSystemStep : undefined,
      }
    }
    case 'safari':
      return {
        title: 'Safari is blocking the microphone',
        steps: [
          'In the menu bar, open Safari, then Settings.',
          'Go to Websites, then Microphone.',
          'Set this site to Allow.',
          'Reload the page and hold the mic again.',
        ],
        note: macSystemStep,
      }
    case 'firefox':
    case 'firefox-ios':
      return {
        title: 'Firefox is blocking the microphone',
        steps: [
          'Click the padlock in the address bar.',
          'Find the blocked microphone permission and clear it with the X next to it.',
          'Reload the page and hold the mic again.',
        ],
        paste: {
          label: 'Or paste this into the address bar, then find Microphone:',
          value: 'about:preferences#privacy',
        },
        note: MAC ? macSystemStep : undefined,
      }
    default:
      return {
        title: 'Your browser is blocking the microphone',
        steps: [
          'Open the site permissions for this page, usually behind the icon next to the web address.',
          'Allow the microphone.',
          'Reload the page and hold the mic again.',
        ],
      }
  }
}

// Asked before we try, so somebody who has already denied it gets the
// instructions instead of a button that appears to do nothing. Returns
// 'granted', 'denied', 'prompt' or null when the browser will not say, which
// Safari historically does not.
export async function micPermissionState() {
  try {
    if (!navigator.permissions?.query) return null
    const status = await navigator.permissions.query({ name: 'microphone' })
    return status.state
  } catch {
    return null
  }
}
